import * as stylex from "@stylexjs/stylex";
import {
  GO_BOARD_FLUID_GRID_LINE_WIDTH_PX,
  GO_BOARD_GRID_END_LINE_INDEX,
  GO_BOARD_GRID_LINE_COLOR,
  GO_BOARD_GRID_LINE_INDEXES,
  GO_BOARD_GRID_LINE_WIDTH_PX,
  GO_BOARD_GRID_START_LINE_INDEX,
  GO_BOARD_GRID_STEP_PERCENT,
  GO_BOARD_MONOCHROME_GRID_LINE_COLOR,
  GO_BOARD_STONE_TRANSITION_DURATION_MS,
} from "@/constants";

const goBoardStonePlace = stylex.keyframes({
  "0%": {
    opacity: 0,
    scale: "0.2",
    translate: "-50% -72%",
  },
  "68%": {
    opacity: 1,
    scale: "1.08",
    translate: "-50% -54%",
  },
  "100%": {
    opacity: 1,
    scale: "1",
    translate: "-50% -50%",
  },
});

const goBoardStyles = stylex.create({
  board: {
    position: "relative",
    overflow: "hidden",
    borderRadius: "11px",
    backgroundColor: "#d9a45f",
  },
  colorShadow: {
    boxShadow: "0 1px 2px rgba(75,44,13,0.18), inset 0 1px 0 rgba(255,255,255,0.28)",
  },
  monochromeShadow: {
    boxShadow: "0 1px 2px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)",
  },
  fluid: {
    aspectRatio: "1 / 1",
    width: "100%",
    borderWidth: "2px",
    borderStyle: "solid",
    borderColor: "#b9803f",
  },
  miniature: {
    width: "72px",
    height: "72px",
  },
  grid: {
    position: "absolute",
    inset: "9.7%",
  },
  verticalGridLine: {
    position: "absolute",
    top: 0,
    bottom: 0,
  },
  verticalGridLineCentered: {
    translate: "-50%",
  },
  verticalGridLineAtEnd: {
    translate: "-100%",
  },
  horizontalGridLine: {
    position: "absolute",
    left: 0,
    right: 0,
  },
  horizontalGridLineCentered: {
    translate: "0 -50%",
  },
  horizontalGridLineAtEnd: {
    translate: "0 -100%",
  },
  verticalGridLinePosition: (leftPercent: number, widthPx: number, lineColor: string) => ({
    left: `${leftPercent}%`,
    width: `${widthPx}px`,
    backgroundColor: lineColor,
  }),
  horizontalGridLinePosition: (topPercent: number, heightPx: number, lineColor: string) => ({
    top: `${topPercent}%`,
    height: `${heightPx}px`,
    backgroundColor: lineColor,
  }),
  stone: {
    position: "absolute",
    translate: "-50% -50%",
    borderRadius: "calc(infinity * 1px)",
    transitionProperty: {
      default: "opacity, scale",
      "@media (prefers-reduced-motion: reduce)": "none",
    },
    transitionTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)",
  },
  stoneSize: {
    width: "10.3%",
    height: "10.3%",
  },
  stonePlacement: {
    animationName: {
      default: goBoardStonePlace,
      "@media (prefers-reduced-motion: reduce)": "none",
    },
    animationDuration: "420ms",
    animationTimingFunction: "cubic-bezier(0.2, 0.85, 0.3, 1.25)",
    animationFillMode: "both",
  },
  blackStone: {
    backgroundColor: "#171717",
    boxShadow: "inset 0 1px 1px rgba(255,255,255,0.16), 0 1px 1px rgba(51,30,10,0.3)",
  },
  whiteStone: {
    backgroundColor: "#f7f4ed",
    boxShadow: "inset 0 1px 1px white, 0 1px 1px rgba(51,30,10,0.32)",
  },
  stoneVisible: {
    scale: "1",
    opacity: 1,
  },
  stoneHidden: {
    scale: "0",
    opacity: 0,
  },
  stonePosition: (leftPercent: number, topPercent: number, transitionDurationMs: number) => ({
    left: `${leftPercent}%`,
    top: `${topPercent}%`,
    transitionDuration: `${transitionDurationMs}ms`,
  }),
  latestMoveMarker: {
    position: "absolute",
    inset: "30%",
    borderRadius: "calc(infinity * 1px)",
    borderWidth: "2px",
    borderStyle: "solid",
  },
  blackStoneLatestMoveMarker: {
    borderColor: "#f7f4ed",
  },
  whiteStoneLatestMoveMarker: {
    borderColor: "#171717",
  },
});

const GoBoard = ({
  isAnimated = false,
  latestMove = null,
  ref,
  size = "miniature",
  stones,
  styles: styleOverrides,
  variant = "color",
  visibleStoneCount = stones.length,
  ...props
}: GoBoardProps) => {
  const isMonochrome = variant === "monochrome";
  const gridLineColor = isMonochrome
    ? GO_BOARD_MONOCHROME_GRID_LINE_COLOR
    : GO_BOARD_GRID_LINE_COLOR;
  const gridLineWidthPx =
    size === "fluid" ? GO_BOARD_FLUID_GRID_LINE_WIDTH_PX : GO_BOARD_GRID_LINE_WIDTH_PX;

  return (
    <div
      ref={ref}
      {...props}
      {...stylex.props(
        goBoardStyles.board,
        size === "fluid" ? goBoardStyles.fluid : goBoardStyles.miniature,
        isMonochrome ? goBoardStyles.monochromeShadow : goBoardStyles.colorShadow,
        styleOverrides,
      )}
    >
      <div {...stylex.props(goBoardStyles.grid)}>
        {GO_BOARD_GRID_LINE_INDEXES.map((gridLineIndex) => {
          const isStartLine = gridLineIndex === GO_BOARD_GRID_START_LINE_INDEX;
          const isEndLine = gridLineIndex === GO_BOARD_GRID_END_LINE_INDEX;

          return (
            <span key={gridLineIndex}>
              <span
                {...stylex.props(
                  goBoardStyles.verticalGridLine,
                  !isStartLine && !isEndLine && goBoardStyles.verticalGridLineCentered,
                  isEndLine && goBoardStyles.verticalGridLineAtEnd,
                  goBoardStyles.verticalGridLinePosition(
                    gridLineIndex * GO_BOARD_GRID_STEP_PERCENT,
                    gridLineWidthPx,
                    gridLineColor,
                  ),
                )}
              />
              <span
                {...stylex.props(
                  goBoardStyles.horizontalGridLine,
                  !isStartLine && !isEndLine && goBoardStyles.horizontalGridLineCentered,
                  isEndLine && goBoardStyles.horizontalGridLineAtEnd,
                  goBoardStyles.horizontalGridLinePosition(
                    gridLineIndex * GO_BOARD_GRID_STEP_PERCENT,
                    gridLineWidthPx,
                    gridLineColor,
                  ),
                )}
              />
            </span>
          );
        })}

        {stones.map((stone, stoneIndex) => {
          const isLatestMove =
            latestMove?.column === stone.column &&
            latestMove.row === stone.row &&
            stoneIndex < visibleStoneCount;

          return (
            <span
              data-go-stone={stone.color}
              key={`${stone.column}-${stone.row}-${stone.color}`}
              {...stylex.props(
                goBoardStyles.stone,
                goBoardStyles.stoneSize,
                isAnimated && goBoardStyles.stonePlacement,
                stone.color === "black" ? goBoardStyles.blackStone : goBoardStyles.whiteStone,
                stoneIndex < visibleStoneCount
                  ? goBoardStyles.stoneVisible
                  : goBoardStyles.stoneHidden,
                goBoardStyles.stonePosition(
                  stone.column * GO_BOARD_GRID_STEP_PERCENT,
                  stone.row * GO_BOARD_GRID_STEP_PERCENT,
                  GO_BOARD_STONE_TRANSITION_DURATION_MS,
                ),
              )}
            >
              {isLatestMove && (
                <span
                  {...stylex.props(
                    goBoardStyles.latestMoveMarker,
                    stone.color === "black"
                      ? goBoardStyles.blackStoneLatestMoveMarker
                      : goBoardStyles.whiteStoneLatestMoveMarker,
                  )}
                />
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
};

GoBoard.displayName = "GoBoard";

export { GoBoard, goBoardStyles };
